import { useState } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta } from "@/design/primitives";

/**
 * NoteOnCard — the "add a note" affordance shared by every board page.
 * Opens a one-line input inline; the note rides the existing note verb and
 * lands in the item's record. Used by Home, Work, Breakage, Email, Calendar.
 * Props:
 *   board    the parent's useBoard() hook object (not a board name)
 *   slug     the row slug to target
 *   testId   passthrough
 *   compact  smaller spacing
 */
export const NoteOnCard = ({ board, slug, testId = "card-note", compact = false }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Jeff 2026-11-03: a note he leaves should still be VISIBLE after he submits
  // it — not just a flash of "Saved". Notes ride back on the board row's
  // payload.jeff.notes (act() merges the response overlay), so render whatever
  // the row currently holds, found here by slug from the live items list.
  const row = (board?.items || []).find((r) => r?.slug === slug);
  const notes = row?.payload?.jeff?.notes || [];
  const lastNote = notes[notes.length - 1];

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    const res = await board.act("note", slug, t);
    setBusy(false);
    if (res) {
      setText("");
      setOpen(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } else {
      setError("That didn't go through — try again.");
    }
  };

  if (!open) {
    // No note affordance in baked/offline mode — signed-out = baked mode and a note
    // would just retry and error.
    if (!board?.live) {
      return null;
    }
    const notesBlock = lastNote ? (
      <div
        data-testid={`${testId}-notes`}
        className={cn("mb-1.5 rounded-control bg-os-wash px-2.5 py-1.5", compact && "px-2 py-1")}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="block truncate font-mono text-[11px] text-os-ink-3">
          “{lastNote.note}”
        </span>
        {notes.length > 1 && (
          <span className="font-mono text-[10px] text-os-ink-4">
            {notes.length} notes on record
          </span>
        )}
      </div>
    ) : null;
    return (
      <>
      {notesBlock}
      <span className={cn("inline-flex items-center gap-2", compact && "gap-1.5")}>
        <button
          type="button"
          data-testid={testId}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          className="rounded-control px-2 py-1 font-mono text-[11px] text-os-ink-3 hover:bg-os-wash hover:text-os-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink"
          aria-label={`Add a note to this card`}
        >
          Add note
        </button>
        {saved && <MonoMeta data-testid={`${testId}-saved`}>Saved</MonoMeta>}
      </span>
      </>
    );
  }

  return (
    <>
    {lastNote && (
      <div
        data-testid={`${testId}-notes`}
        className={cn("mb-1.5 rounded-control bg-os-wash px-2.5 py-1.5", compact && "px-2 py-1")}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="block truncate font-mono text-[11px] text-os-ink-3">
          “{lastNote.note}”
        </span>
        {notes.length > 1 && (
          <span className="font-mono text-[10px] text-os-ink-4">
            {notes.length} notes on record
          </span>
        )}
      </div>
    )}
    <form
      data-testid={`${testId}-form`}
      onClick={(e) => e.stopPropagation()}
      onSubmit={send}
      className="flex items-center gap-2"
    >
      <input
        data-testid={`${testId}-input`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write a note for the record…"
        autoFocus
        className="min-w-[12rem] flex-1 rounded-control border border-os-border bg-white px-2.5 py-1.5 text-[13px] outline-none placeholder:text-os-ink-4 focus:border-os-border-strong"
      />
      <button
        type="submit"
        data-testid={`${testId}-send`}
        disabled={busy || !text.trim()}
        className="rounded-control border border-os-border px-2.5 py-1.5 font-mono text-[12px] text-os-ink hover:border-os-border-strong disabled:opacity-50"
      >
        {busy ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="rounded-control px-2 py-1.5 font-mono text-[12px] text-os-ink-3 hover:text-os-ink"
      >
        Cancel
      </button>
      {error && (
        <span data-testid={`${testId}-error`} className="text-[12px] text-os-red">{error}</span>
      )}
    </form>
    </>
  );
};

export default NoteOnCard;